import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { calculateAnnualWaste } from '@/lib/meeting-waste/calculations'
import { asyncStandupPath, meetingWastePath, signInPath } from '@/routes'
import { Link } from '@inertiajs/react'
import { ArrowRight, HelpCircleIcon } from 'lucide-react'

const TEAM_SIZES = [3, 5, 8, 12, 20] as const
const HOURLY_RATES = [75, 100, 125, 150] as const

const RATE_LABELS: Record<(typeof HOURLY_RATES)[number], string> = {
  75: 'Mid-level (~$155k loaded)',
  100: 'Senior (~$210k loaded)',
  125: 'Staff (~$260k loaded)',
  150: 'Staff+ (~$310k loaded)',
}

const linkClass = 'text-foreground decoration-golden-500 underline underline-offset-4'

type Faq = {
  question: string
  answer: string
}

function formatUsd(amount: number) {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
}

function formatHours(hours: number) {
  return hours.toLocaleString('en-US')
}

function standupCost(teamSize: number, hourlyRate: number) {
  return calculateAnnualWaste({
    numberOfTeams: 1,
    avgTeamSize: teamSize,
    meetingsPerWeek: 5,
    minutesPerMeeting: 15,
    hourlyRate,
    attendanceRate: 1,
    overrunFactor: 1,
  })
}

function standupHours(teamSize: number) {
  // Standup cost is hours × hourly rate, so a $1 rate returns the hours.
  return standupCost(teamSize, 1)
}

export function DailyStandupCostArticle({ headline, faqs }: { headline: string; faqs: Faq[] }) {
  return (
    <div className="mx-auto max-w-4xl space-y-12 md:space-y-16">
      <header className="space-y-4 text-center">
        <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">Daily standup cost</p>
        <h1 className="text-foreground text-4xl font-bold tracking-tight text-balance sm:text-5xl md:text-6xl">
          {headline}
        </h1>
        <p className="text-muted-foreground mx-auto max-w-2xl text-lg leading-relaxed sm:text-xl">
          A 15-minute standup is a small block on the calendar. This meeting time calculator adds it up: how much time
          is wasted across a year, then the daily standup cost of those hours at fully-loaded US engineering salaries.
        </p>
      </header>

      <Card className="border-golden-500/20 from-golden-500/5 via-background to-muted/30 bg-gradient-to-br p-6 text-center sm:p-8">
        <p className="text-muted-foreground text-sm leading-relaxed sm:text-base">
          The hours table shows how much time is wasted in a 15-minute daily standup. The salary table is the daily
          standup cost of those hours. For another salary, length, or attendance, use the{' '}
          <Link href={meetingWastePath()} className={`${linkClass} font-medium`}>
            wasted money meeting calculator
          </Link>
          .
        </p>
        <div className="mt-5">
          <Button asChild size="lg" className="bg-golden-500 hover:bg-golden-600 font-semibold text-black">
            <Link href={meetingWastePath()}>
              Open the money calculator
              <ArrowRight className="ml-2 size-4 shrink-0" />
            </Link>
          </Button>
        </div>
      </Card>

      <section id="time-wasted" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">How much time is wasted</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Hours per year = people × 5 standups a week × (15 ÷ 60) hours × 48 working weeks. Vacation is already baked
            in as 48 weeks, not 52. One person spends 60 hours. The table is that same meeting for the whole team.
          </p>
          <p>
            This is a worked example from those inputs: 15 minutes, full attendance, no overrun. The number is time
            spent in the meeting.
          </p>
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              Meeting time calculator, worked example: 15 minutes, 5 days a week, 48 weeks, 100% attendance, no
              overrun. Highlighted row is a five-person team (300 hours).
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Team size
                </th>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Hours a year
                </th>
              </tr>
            </thead>
            <tbody>
              {TEAM_SIZES.map((size) => {
                const highlight = size === 5
                return (
                  <tr key={size} className="border-t">
                    <th scope="row" className="text-foreground px-4 py-3 font-medium">
                      {size} engineers
                    </th>
                    <td
                      className={
                        highlight
                          ? 'bg-golden-500/15 text-foreground px-4 py-3 font-semibold'
                          : 'text-muted-foreground px-4 py-3'
                      }
                    >
                      {formatHours(standupHours(size))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section id="standup-cost-table" className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Daily standup cost of that time</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            Annual standup cost = the hours above × a fully-loaded hourly salary. Five people at $100/hour is 300 ×
            $100, which is the highlighted $30,000 cell.
          </p>
          <p>
            Hourly rates are <strong className="text-foreground">US engineering fully-loaded bands</strong>: cash
            compensation plus benefits and overhead, not take-home pay. A $150k engineer often lands near $75 to
            $120/hour once loaded. Figures are modeled meeting cost from the inputs above, not Git Digest traffic or
            revenue.
          </p>
        </div>

        <div className="overflow-x-auto rounded-xl border">
          <table className="w-full min-w-[36rem] text-left text-sm">
            <caption className="bg-muted/40 text-muted-foreground px-4 py-3 text-left text-xs leading-relaxed">
              Base daily standup: 15 minutes, 5 days a week, 48 weeks, 100% attendance, no overrun. Highlighted cell
              matches a five-person team at $100/hour.
            </caption>
            <thead className="bg-muted/50 text-foreground">
              <tr>
                <th scope="col" className="px-4 py-3 font-semibold">
                  Team size
                </th>
                {HOURLY_RATES.map((rate) => (
                  <th key={rate} scope="col" className="px-4 py-3 font-semibold">
                    {formatUsd(rate)}/hr
                    <span className="text-muted-foreground mt-0.5 block text-xs font-normal">{RATE_LABELS[rate]}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TEAM_SIZES.map((size) => (
                <tr key={size} className="border-t">
                  <th scope="row" className="text-foreground px-4 py-3 font-medium">
                    {size} engineers
                  </th>
                  {HOURLY_RATES.map((rate) => {
                    const highlight = size === 5 && rate === 100
                    return (
                      <td
                        key={rate}
                        className={
                          highlight
                            ? 'bg-golden-500/15 text-foreground px-4 py-3 font-semibold'
                            : 'text-muted-foreground px-4 py-3'
                        }
                      >
                        {formatUsd(standupCost(size, rate))}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-muted-foreground text-sm leading-relaxed">
          The default 1.2× overrun on the{' '}
          <Link href={meetingWastePath()} className={linkClass}>
            wasted money meeting calculator
          </Link>{' '}
          raises that highlighted $30,000 cell to $36,000 and the 300 hours to 360. Use it when the standup runs long,
          or when you want a different length for planning or the weekly sync.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">What 300 hours and $30,000 buy</h2>
        <div className="text-muted-foreground space-y-4 text-base leading-relaxed">
          <p>
            A five-person daily standup at $100/hour fully loaded is the highlighted $30,000. Twelve people at
            $150/hour is $108,000 on the same table.
          </p>
          <p>
            Git Digest replaces that status round with{' '}
            <Link href="/" className={linkClass}>
              team GitHub and Slack commit digests
            </Link>
            . The source of truth is what landed in git.
          </p>
          <p>
            The{' '}
            <Link href={asyncStandupPath()} className={linkClass}>
              async standup playbook
            </Link>{' '}
            covers how to roll that out: cadence, timezones, blockers, and where form bots fall short.
          </p>
        </div>
      </section>

      <section className="space-y-6">
        <div>
          <h2 className="text-foreground text-3xl tracking-tight sm:text-4xl">Meeting time calculator FAQ</h2>
          <p className="text-muted-foreground mt-3 text-base">
            Common questions about time wasted in meetings, standup cost, and replacing status meetings with digests.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {faqs.map((faq) => (
            <Card key={faq.question} className="h-full">
              <CardContent className="pt-6">
                <h3 className="text-foreground mb-3 flex items-start gap-x-3 text-lg">
                  <span className="flex h-[1lh] items-center">
                    <HelpCircleIcon className="text-golden-500 size-4 shrink-0" />
                  </span>
                  {faq.question}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <Card className="border-orange-200/50 bg-gradient-to-br from-orange-50 to-orange-100/30 p-8 text-center dark:border-orange-800/30 dark:from-orange-500/10 dark:to-orange-500/5">
        <h2 className="mb-3 text-2xl text-gray-900 dark:text-white">Replace the standup with GitHub digests</h2>
        <p className="text-muted-foreground mx-auto mb-6 max-w-2xl text-sm">
          Connect GitHub and get daily or weekly team commit summaries in Slack and email. Same alignment, without the
          standup on the calendar. Then{' '}
          <Link href={meetingWastePath()} className={linkClass}>
            price any other meeting
          </Link>
          .
        </p>
        <Button
          asChild
          size="lg"
          className="bg-golden-500 hover:bg-golden-600 font-semibold text-black shadow-lg hover:shadow-xl"
        >
          <Link href={`${signInPath()}?utm_source=daily-standup-cost&utm_campaign=standup-cost`}>
            Start with GitHub
            <ArrowRight className="ml-2 size-4 shrink-0" />
          </Link>
        </Button>
      </Card>
    </div>
  )
}
